'use client'

import { wealthScoreBand } from '@/features/cases'

/**
 * Ported from `nivex-design/apps/nivex-web-v3/src/pages/dashboard/DashboardHome.tsx`'s
 * `SegmentedGauge`.
 *
 * A dial built from discrete rounded blocks that radiate outward from the centre (each one a
 * rotated rounded rect, not an arc stroke — arc strokes bend along the curve and read as
 * sausages rather than the distinct tiles this design calls for).
 *
 * Each block is coloured by the Wealth Score band its own position falls into, so the dial reads
 * as a red → amber → green health ramp rather than a brand-coloured bar: the colour where the fill
 * stops *is* the band you're in. 15 blocks so all five 20-point bands own exactly three each and
 * the colour steps land on the real band boundaries. Unfilled blocks drop to a neutral track.
 */
export function SegmentedGauge({ score }: { score: number }) {
  const SEGMENT_COUNT = 15
  const radius = 84
  const blockW = 15
  const blockH = 30
  const cx = 112
  const cy = 110
  const filledCount = Math.round((Math.max(0, Math.min(100, score)) / 100) * SEGMENT_COUNT)

  // The guide ring is the outermost thing drawn, so the viewBox has to clear it or the crown of
  // the arc gets clipped — hence cy (110) > guideR (106), rather than the reverse.
  const guideR = radius + blockH / 2 + 7
  // Derive the dash pattern from the true arc length so a whole number of dots spans it exactly;
  // a fixed dasharray leaves a stray part-dot wherever the pattern happens to run out.
  const DOT_COUNT = 56
  const DOT_LEN = 1.5
  const dotGap = (Math.PI * guideR) / DOT_COUNT - DOT_LEN

  return (
    <svg viewBox="0 0 224 124" className="mx-auto block w-full" aria-hidden="true">
      {/* Faint dotted arc just outside the blocks, as in the reference */}
      <path
        d={`M ${cx - guideR} ${cy} A ${guideR} ${guideR} 0 0 1 ${cx + guideR} ${cy}`}
        fill="none"
        // --border (not --border-primary) keeps the decorative ring a step fainter than the
        // empty blocks, so the two don't read as one flat mass.
        stroke="var(--border)"
        strokeWidth={1.5}
        strokeDasharray={`${DOT_LEN} ${dotGap}`}
        strokeLinecap="round"
      />
      {Array.from({ length: SEGMENT_COUNT }, (_, i) => {
        // Sweep left→right through the top: -90° is 9 o'clock, +90° is 3 o'clock.
        const angle = -90 + (i + 0.5) * (180 / SEGMENT_COUNT)
        const isFilled = i < filledCount
        // Score this block stands for — its own band decides its colour.
        const segmentScore = ((i + 0.5) / SEGMENT_COUNT) * 100
        return (
          <rect
            key={i}
            x={-blockW / 2}
            y={-radius - blockH / 2}
            width={blockW}
            height={blockH}
            rx={6}
            transform={`translate(${cx} ${cy}) rotate(${angle})`}
            // Empty track uses border-primary, not --muted: --muted is near-white in light mode.
            fill={isFilled ? wealthScoreBand(segmentScore).color : 'var(--border-primary)'}
          />
        )
      })}
    </svg>
  )
}
